import { BoxType } from '@lite-workspace/dto-fotoclass-templates';
import type {
  CustomSmartObjectTemplateBox,
  SmartObjectBoxParams,
  TransformMatrix,
} from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableObjectData } from '../../interfaces/drawable-data.interface';
import type { RectangleData } from '../../interfaces/rectangle-data.interface';
import { buildBoxPassthroughMeta, carriedBoxFields, mergeBoxPassthroughMeta } from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

function rectsEqual(a: RectangleData | undefined, b: RectangleData): boolean {
  return !!a && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height && a.angle === b.angle;
}

/**
 * Hybrid de-escalation (design §3 #3) — BoxType.SMART_OBJECT decodes onto the SAME
 * DrawableObjectData shape as BoxType.OBJECT (generateDrawableObject serves both with one
 * function already), distinguished only by `objectMeta.boxType`. See object-box.ts, which
 * never populates objectMeta — that is this file's territory.
 */
export const decode: BoxDecoder<CustomSmartObjectTemplateBox, DrawableObjectData> = (box, ctx) => {
  if (box.params.pixelData !== undefined) {
    ctx.drop('pixelData removed in Phase 3 — persisted asset lives in versions/url');
  }

  if (box.params.originalWidth !== undefined || box.params.originalHeight !== undefined) {
    ctx.warn('originalWidth/originalHeight have no persisted home on DrawableObjectData — dropped');
  }

  if (box.params.sourceRef !== undefined) {
    ctx.warn('sourceRef has no persisted home on DrawableObjectData — dropped');
  }

  const hasData = box.params.url !== undefined || box.params.fileId !== undefined || box.params.versions !== undefined;
  const container = rectFromBox(box.rect, ctx);

  return {
    id: box.id,
    type: 'object',
    ...carriedBoxFields(box),
    order: 0, // dispatcher проставляет реальный order по индексу в collage
    canResize: false,
    opacity: box.opacity,
    blendMode: box.blendMode,
    visible: box.visible,
    locked: box.locked,
    rotation: box.rotation,
    // box-level effects (shadow/stroke on the smart-object's OWN box) — distinct from params.rawEffects
    // below, which is the smart-object's embedded pre-conversion snapshot, not this box's live effect.
    effects: buildDrawableEffects(box.effects),
    data: hasData ? { url: box.params.url, fileId: box.params.fileId, versions: box.params.versions } : undefined,
    maskDataUrl: box.params.maskDataUrl,
    maskUrl: box.params.maskUrl,
    maskFileId: box.params.maskFileId,
    objectMeta: {
      boxType: 'smartObject',
      sourceType: box.params.sourceType,
      sourceId: box.params.sourceId,
      rawEffects: box.params.rawEffects,
      rawTransform: box.params.transform,
      snapshotRect: container,
    },
    naturalAligns: {
      container,
      content: box.contentRect ? rectFromBox(box.contentRect, ctx.child('contentRect')) : undefined,
    },
    templateMeta: buildBoxPassthroughMeta(box),
  };
};

/** Params of a SMART_OBJECT box from its drawable; a stale `rawTransform` is replaced by a translation-only one. */
export function smartObjectParamsOf(drawable: DrawableObjectData, warn: (reason: string) => void): SmartObjectBoxParams {
  const meta = drawable.objectMeta;
  const container = drawable.naturalAligns.container;
  const transformIsFresh = rectsEqual(meta?.snapshotRect, container);

  let transform: TransformMatrix | undefined;
  if (meta?.rawTransform) {
    if (transformIsFresh) {
      transform = meta.rawTransform as unknown as TransformMatrix;
    } else {
      warn('rawTransform snapshotRect diverged from the live container — synthesizing a translation-only transform');
      transform = [1, 0, 0, 1, container.left, container.top];
    }
  }

  return {
    url: drawable.data?.url,
    fileId: drawable.data?.fileId,
    versions: drawable.data?.versions,
    sourceType: meta?.sourceType,
    sourceId: meta?.sourceId,
    rawEffects: meta?.rawEffects,
    transform,
    maskDataUrl: drawable.maskDataUrl,
    maskUrl: drawable.maskUrl,
    maskFileId: drawable.maskFileId,
  };
}

export const encode: BoxEncoder<DrawableObjectData, CustomSmartObjectTemplateBox> = (drawable, ctx) => {
  const container = drawable.naturalAligns.container;
  const params = smartObjectParamsOf(drawable, reason => ctx.warn(reason));
  const box: CustomSmartObjectTemplateBox = {
    type: BoxType.SMART_OBJECT,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(container, ctx),
    contentRect: drawable.naturalAligns.content ? rectToBox(drawable.naturalAligns.content, ctx) : undefined,
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    params,
  };

  return mergeBoxPassthroughMeta(box, drawable.templateMeta);
};
